import exifr from 'exifr';
import { CapturedPhoto } from '../utils/camera';

export interface PhotoMetadata {
  // EXIF Basic Information
  fileName?: string;
  fileSize?: number;
  mimeType?: string;

  // Date and Time Information
  dateTimeOriginal?: Date;
  dateTimeDigitized?: Date;
  dateTimeModified?: Date;
  createDate?: Date;
  modifyDate?: Date;

  // GPS Information
  gpsCoordinates?: {
    latitude: number;
    longitude: number;
    altitude?: number;
    accuracy?: number;
  };
  gpsDateTime?: Date;

  // Camera Information
  cameraMake?: string;
  cameraModel?: string;
  lensModel?: string;
  lensMake?: string;

  // Image Properties
  imageWidth?: number;
  imageHeight?: number;
  orientation?: number;
  resolution?: {
    x: number;
    y: number;
    unit?: string;
  };

  // Technical Details
  software?: string;
  artist?: string;
  copyright?: string;

  // Camera Settings
  iso?: number;
  exposureTime?: number;
  fNumber?: number;
  focalLength?: number;
  flash?: boolean;
  whiteBalance?: string;

  // Processing Information
  colorSpace?: string;
  compression?: string;

  // Editing Detection
  isEdited?: boolean;
  editIndicators?: string[];

  // Quality Assessment
  hasExifData?: boolean;
  hasGpsData?: boolean;
  hasCameraData?: boolean;
  dataQuality?: 'excellent' | 'good' | 'fair' | 'poor' | 'none';
}

export interface MetadataExtractionResult {
  success: boolean;
  metadata: PhotoMetadata;
  error?: string;
  processingTime: number;
}

/**
 * Extract comprehensive metadata from an image file
 */
export async function extractPhotoMetadata(file: File | Blob): Promise<MetadataExtractionResult> {
  const startTime = performance.now();

  try {
    const metadata: PhotoMetadata = {};

    // Parse EXIF data with comprehensive options
    const exifData = await exifr.parse(file, {
      gps: true,
      exif: true,
      xmp: true,
      iptc: true,
      jfif: true,
      sanitize: true,
      translateKeys: true,
      reviveValues: true
    });

    if (exifData) {
      // Date and Time Information
      if (exifData.DateTimeOriginal) metadata.dateTimeOriginal = exifData.DateTimeOriginal;
      if (exifData.DateTimeDigitized) metadata.dateTimeDigitized = exifData.DateTimeDigitized;
      if (exifData.DateTime) metadata.dateTimeModified = exifData.DateTime;
      if (exifData.CreateDate) metadata.createDate = exifData.CreateDate;
      if (exifData.ModifyDate) metadata.modifyDate = exifData.ModifyDate;

      // GPS Information
      if (exifData.latitude && exifData.longitude) {
        metadata.gpsCoordinates = {
          latitude: exifData.latitude,
          longitude: exifData.longitude,
          altitude: exifData.GPSAltitude,
          accuracy: exifData.GPSHPositioningError
        };

        if (exifData.GPSDateStamp && exifData.GPSTimeStamp) {
          // Combine GPS date and time
          metadata.gpsDateTime = new Date(`${exifData.GPSDateStamp} ${exifData.GPSTimeStamp}`);
        }
      }

      // Camera Information
      if (exifData.Make) metadata.cameraMake = exifData.Make;
      if (exifData.Model) metadata.cameraModel = exifData.Model;
      if (exifData.LensModel) metadata.lensModel = exifData.LensModel;
      if (exifData.LensMake) metadata.lensMake = exifData.LensMake;

      // Image Properties
      if (exifData.ExifImageWidth) metadata.imageWidth = exifData.ExifImageWidth;
      if (exifData.ExifImageHeight) metadata.imageHeight = exifData.ExifImageHeight;
      if (exifData.Orientation) metadata.orientation = exifData.Orientation;
      if (exifData.XResolution && exifData.YResolution) {
        metadata.resolution = {
          x: exifData.XResolution,
          y: exifData.YResolution,
          unit: exifData.ResolutionUnit || 'dpi'
        };
      }

      // Technical Details
      if (exifData.Software) metadata.software = exifData.Software;
      if (exifData.Artist) metadata.artist = exifData.Artist;
      if (exifData.Copyright) metadata.copyright = exifData.Copyright;

      // Camera Settings
      if (exifData.ISO) metadata.iso = exifData.ISO;
      if (exifData.ExposureTime) metadata.exposureTime = exifData.ExposureTime;
      if (exifData.FNumber) metadata.fNumber = exifData.FNumber;
      if (exifData.FocalLength) metadata.focalLength = exifData.FocalLength;
      if (exifData.Flash !== undefined) metadata.flash = exifData.Flash;
      if (exifData.WhiteBalance) metadata.whiteBalance = exifData.WhiteBalance;

      // Processing Information
      if (exifData.ColorSpace) metadata.colorSpace = exifData.ColorSpace;
      if (exifData.Compression) metadata.compression = exifData.Compression;

      // Edit Detection
      const editAnalysis = detectPhotoEditing(exifData);
      metadata.isEdited = editAnalysis.isEdited;
      metadata.editIndicators = editAnalysis.indicators;

      // Quality Assessment
      metadata.hasExifData = true;
      metadata.hasGpsData = !!metadata.gpsCoordinates;
      metadata.hasCameraData = !!(metadata.cameraMake || metadata.cameraModel);
      metadata.dataQuality = assessDataQuality(metadata);
    }

    // Add basic file information
    if (file instanceof File) {
      metadata.fileName = file.name;
      metadata.fileSize = file.size;
      metadata.mimeType = file.type;
    }

    const endTime = performance.now();

    return {
      success: true,
      metadata,
      processingTime: endTime - startTime
    };

  } catch (error) {
    const endTime = performance.now();

    // Return basic metadata even if EXIF parsing fails
    const basicMetadata: PhotoMetadata = {
      fileName: file instanceof File ? file.name : undefined,
      fileSize: file instanceof File ? file.size : undefined,
      mimeType: file instanceof File ? file.type : undefined,
      hasExifData: false,
      hasGpsData: false,
      hasCameraData: false,
      dataQuality: 'none',
      isEdited: false,
      editIndicators: ['No EXIF data available']
    };

    return {
      success: false,
      metadata: basicMetadata,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
      processingTime: endTime - startTime
    };
  }
}

/**
 * Detect signs of photo editing based on metadata
 */
function detectPhotoEditing(exifData: any): { isEdited: boolean; indicators: string[] } {
  const indicators: string[] = [];

  // Software that suggests editing
  if (exifData.Software) {
    const editingSoftware = [
      'photoshop', 'gimp', 'lightroom', 'snapseed', 'vsco',
      'instagram', 'facetune', 'picsart', 'canva', 'fotor'
    ];

    if (editingSoftware.some(software =>
      exifData.Software.toLowerCase().includes(software)
    )) {
      indicators.push(`Editing software detected: ${exifData.Software}`);
    }
  }

  // Missing expected EXIF data
  if (!exifData.DateTimeOriginal && !exifData.CreateDate) {
    indicators.push('Missing creation timestamp');
  }

  // Inconsistent timestamps
  if (exifData.DateTimeOriginal && exifData.DateTime) {
    const timeDiff = Math.abs(
      new Date(exifData.DateTimeOriginal).getTime() -
      new Date(exifData.DateTime).getTime()
    );
    if (timeDiff > 24 * 60 * 60 * 1000) { // More than 24 hours difference
      indicators.push('Inconsistent timestamp data');
    }
  }

  // Unusual aspect ratios (might indicate cropping)
  if (exifData.ExifImageWidth && exifData.ExifImageHeight) {
    const aspectRatio = exifData.ExifImageWidth / exifData.ExifImageHeight;
    if (aspectRatio < 0.5 || aspectRatio > 2.0) {
      indicators.push('Unusual aspect ratio (possibly cropped)');
    }
  }

  return {
    isEdited: indicators.length > 0,
    indicators
  };
}

/**
 * Assess the quality and completeness of extracted metadata
 */
function assessDataQuality(metadata: PhotoMetadata): PhotoMetadata['dataQuality'] {
  let score = 0;
  const maxScore = 10;

  // GPS data (3 points)
  if (metadata.hasGpsData) score += 3;

  // Camera data (2 points)
  if (metadata.hasCameraData) score += 2;

  // Timestamp data (2 points)
  if (metadata.dateTimeOriginal || metadata.createDate) score += 2;

  // Technical details (1 point)
  if (metadata.imageWidth && metadata.imageHeight) score += 1;

  // Camera settings (1 point)
  if (metadata.iso || metadata.exposureTime || metadata.fNumber) score += 1;

  // Processing info (1 point)
  if (metadata.software || metadata.colorSpace) score += 1;

  const percentage = (score / maxScore) * 100;

  if (percentage >= 80) return 'excellent';
  if (percentage >= 60) return 'good';
  if (percentage >= 40) return 'fair';
  if (percentage >= 20) return 'poor';
  return 'none';
}

/**
 * Format metadata for JSON storage
 */
export function formatMetadataForStorage(metadata: PhotoMetadata): string {
  const storageData = {
    extracted_at: new Date().toISOString(),
    data_quality: metadata.dataQuality,
    has_exif: metadata.hasExifData,
    has_gps: metadata.hasGpsData,
    has_camera: metadata.hasCameraData,
    is_edited: metadata.isEdited,
    edit_indicators: metadata.editIndicators,

    // Date information
    capture_date: metadata.dateTimeOriginal?.toISOString(),
    digitized_date: metadata.dateTimeDigitized?.toISOString(),
    modified_date: metadata.dateTimeModified?.toISOString(),
    gps_date: metadata.gpsDateTime?.toISOString(),

    // Location information
    gps: metadata.gpsCoordinates ? {
      latitude: metadata.gpsCoordinates.latitude,
      longitude: metadata.gpsCoordinates.longitude,
      altitude: metadata.gpsCoordinates.altitude,
      accuracy: metadata.gpsCoordinates.accuracy
    } : null,

    // Camera information
    camera: {
      make: metadata.cameraMake,
      model: metadata.cameraModel,
      lens_model: metadata.lensModel,
      lens_make: metadata.lensMake
    },

    // Image properties
    image: {
      width: metadata.imageWidth,
      height: metadata.imageHeight,
      orientation: metadata.orientation,
      resolution: metadata.resolution
    },

    // Technical details
    technical: {
      software: metadata.software,
      artist: metadata.artist,
      copyright: metadata.copyright,
      color_space: metadata.colorSpace,
      compression: metadata.compression
    },

    // Camera settings
    settings: {
      iso: metadata.iso,
      exposure_time: metadata.exposureTime,
      f_number: metadata.fNumber,
      focal_length: metadata.focalLength,
      flash: metadata.flash,
      white_balance: metadata.whiteBalance
    },

    // File information
    file: {
      name: metadata.fileName,
      size: metadata.fileSize,
      mime_type: metadata.mimeType
    }
  };

  return JSON.stringify(storageData, null, 2);
}

/**
 * Extract metadata from CapturedPhoto (camera/gallery capture)
 */
export async function extractMetadataFromCapturedPhoto(
  capturedPhoto: CapturedPhoto,
  pictureNumber: number = 1
): Promise<{ pictureNumber: number; metadata: string; processingTime: number }> {
  const result = await extractPhotoMetadata(capturedPhoto.blob);

  const formattedMetadata = formatMetadataForStorage(result.metadata);

  return {
    pictureNumber,
    metadata: formattedMetadata,
    processingTime: result.processingTime
  };
}

/**
 * Validate metadata integrity
 */
export function validateMetadataIntegrity(metadataJson: string): boolean {
  try {
    const parsed = JSON.parse(metadataJson);

    // Check required fields
    const requiredFields = ['extracted_at', 'data_quality'];
    return requiredFields.every(field => Object.prototype.hasOwnProperty.call(parsed, field));
  } catch {
    return false;
  }
}

/**
 * Get metadata summary for display
 */
export function getMetadataSummary(metadataJson: string): string {
  try {
    const metadata = JSON.parse(metadataJson);

    const summary = [];

    if (metadata.has_gps) {
      summary.push('📍 GPS');
    }

    if (metadata.has_camera) {
      summary.push(`📷 ${metadata.camera?.make || 'Unknown'} ${metadata.camera?.model || ''}`.trim());
    }

    if (metadata.capture_date) {
      const date = new Date(metadata.capture_date);
      summary.push(`📅 ${date.toLocaleDateString()}`);
    }

    if (metadata.is_edited) {
      summary.push('⚠️ Edited');
    }

    if (summary.length === 0) {
      summary.push('📄 Basic info only');
    }

    return summary.join(' • ');
  } catch {
    return '📄 Metadata unavailable';
  }
}